import type { HotkeyMap } from './use-hotkeys';

/**
 * Lo que un listener de teclado no puede ver por su cuenta: lo que ligaron los
 * demás.
 *
 * Every `useHotkeys` listens on the same document and keeps its own half-typed
 * sequence, so the shell could be one key into `g c` while a module's plain `c`
 * had no idea. Pressing `g c` from Tareas navigated to Contactos *and* opened
 * the create dialog on the way out — two modules had already patched that in
 * their own corner, each differently. This registry is the shared memory that
 * makes the patch unnecessary: one armed sequence for the whole app, and a way
 * to ask whether anybody at all binds a given sequence.
 */

const maps = new Set<{ current: HotkeyMap }>();

export const SEQUENCE_SEPARATOR = ' ';

export function registerHotkeyMap(ref: { current: HotkeyMap }): () => void {
  maps.add(ref);
  return () => {
    maps.delete(ref);
  };
}

/** Whether some mounted listener answers this exact sequence, e.g. `'g c'`. */
export function isBoundSequence(sequence: string): boolean {
  for (const ref of maps) {
    for (const binding of Object.keys(ref.current)) {
      if (binding.toLowerCase() === sequence) return true;
    }
  }
  return false;
}

/** Whether some mounted listener has a sequence starting with this key. */
export function armsSequence(combo: string): boolean {
  const start = `${combo}${SEQUENCE_SEPARATOR}`;
  for (const ref of maps) {
    for (const binding of Object.keys(ref.current)) {
      if (binding.toLowerCase().startsWith(start)) return true;
    }
  }
  return false;
}

interface ArmedSequence {
  prefix: string;
  /** The keydown that armed it, so its own listeners do not consume it. */
  event: Event;
  /** The keydown that already answered it, if any. */
  answeredBy?: Event;
}

let armed: ArmedSequence | null = null;
let timer: number | undefined;

export const armedSequence = (): ArmedSequence | null => armed;

/**
 * Marks the sequence as answered without forgetting it.
 *
 * The listeners of one keydown run one after another and a microtask can slip
 * between them, so disarming here would hand the same `c` to the next listener
 * as a bare keystroke — the bug this whole file exists to kill. The sequence
 * stays visible for the rest of the dispatch and dies on the next keydown.
 */
export function markSequenceAnswered(event: Event): void {
  if (armed) armed.answeredBy = event;
}

export function disarmSequence(): void {
  armed = null;
  if (timer !== undefined) window.clearTimeout(timer);
  timer = undefined;
}

export function armSequence(prefix: string, event: Event, timeoutMs: number): void {
  disarmSequence();
  armed = { prefix, event };
  timer = window.setTimeout(disarmSequence, timeoutMs);
}
